import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGetPlanQuery } from '../store/api/planApi';

// P5: centralizes the contextual Premium upsell. Free merchants only (never
// super-admin), shown at most ONCE PER BROWSER SESSION PER TRIGGER (sessionStorage).
//
// Usage:
//   const upsell = usePremiumUpsell();
//   upsell.maybeShow('payout_modal', { savings, context });
//   {upsell.isOpen && <PremiumUpsellModal {...upsell.modalProps} onClose={upsell.close} />}
const usePremiumUpsell = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;
  const { data: plan } = useGetPlanQuery(undefined, { skip: isSuperAdmin });
  const isFree = plan?.planKey === 'free';

  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState({});

  const close = useCallback(() => setIsOpen(false), []);

  // Explicit open (e.g. a user clicking an "Upgrade" CTA) — not session-gated.
  const show = useCallback((context = {}) => {
    setModalProps({ savings: context.savings, context: context.context });
    setIsOpen(true);
  }, []);

  const maybeShow = useCallback(
    (trigger, context = {}) => {
      if (isSuperAdmin || !isFree || !trigger) return false;

      const key = `premiumUpsellShown:${trigger}`;
      try {
        if (sessionStorage.getItem(key) === '1') return false;
        sessionStorage.setItem(key, '1');
      } catch {
        /* sessionStorage unavailable — show once in-memory anyway */
      }

      setModalProps({ savings: context.savings, context: context.context });
      setIsOpen(true);
      return true;
    },
    [isSuperAdmin, isFree],
  );

  return { maybeShow, show, modalProps, isOpen, close, isFree, feePct: plan?.feePct ?? null };
};

export default usePremiumUpsell;
